<script setup>
import { onMounted, computed, ref, provide, onBeforeUnmount, onUnmounted, watch  } from 'vue';
import { useRouter, useRoute } from "vue-router";
import { useLoadingStore } from '@/stores/loading';
import { downloadResultPDF } from '@/utils/pdfDownloader.js';
import api from '@/lib/api.js';

// 학생 결과지 컴포넌트
import StudentResultComponent from '@/components/StudentResultComponent.vue';

const router = useRouter();
const route = useRoute();

const shouldShowLayout = computed(() => {   
    const allowedChildNames = ['ResultTeacher', 'ResultStudent', 'ResultPrincipal', 'ResultStatus', 'ResultAnalysis', 'ResultAnalysisElementary']; 
    return route.name && allowedChildNames.includes(route.name);
});

// 헤더 내용 변경
const headerContent = ref(''); 
const updateHeaderContent = (content) => {headerContent.value = content;};
provide('updateHeaderContent', updateHeaderContent); 
onBeforeUnmount(() => {headerContent.value = ''; }); 

// 로딩 추가
const loadingStore = useLoadingStore(); // 메시지/다운로드 상태표시용
const isLoadingOverlay = ref(true); // v-if="isLoadingOverlay"와 연동 
const isReportReadyForDownload = ref(false); // 다운로드 버튼 활성화 여부
const isMainContentVisible = ref(false); // <router-view>를 감싸는 div의 visible 클래스용

const countdownSeconds = ref(0); // 초기 카운트다운 값
let timerInterval = null; 

const downloadProgressMessage = computed(() => {
    if (countdownSeconds.value > 0) {
        return `PDF 다운로드 준비중.. (약 ${countdownSeconds.value}초 소요)`;
    } 
});

// 로딩 시뮬레이션을 시작하는 함수
const startLoadingSimulation = () => { 
    // 이전 타이머가 있다면 해제 (중복 실행 방지)
    if (timerInterval) {
    clearInterval(timerInterval);
    timerInterval = null;
    }

    // 로딩 상태 초기화 및 시작
    isLoadingOverlay.value = true; 
    isReportReadyForDownload.value = false;
    isMainContentVisible.value = false; // 메인 콘텐츠 일단 숨기기
    countdownSeconds.value = 0; // 로딩 시간 리셋  

    timerInterval = setInterval(() => {
        if (countdownSeconds.value > 0) {
            countdownSeconds.value--;      
        } else {
            clearInterval(timerInterval); // 타이머 해제
            isReportReadyForDownload.value = true; // 다운로드 버튼 활성화
            isMainContentVisible.value = true; // 메인 콘텐츠 보이기
            console.log('Loading simulation ended.');
        }
    }, 1000);
};

const setReportDownloadTypeAndMessage = () => { 
  if (route.name === 'ResultTeacher') {
      loadingStore.setLoadingMessages(
        "학급용 검사결과지 다운로드 (7페이지)", 
        '학급 단위로 학생들의 문해력, 수리력의 척도점수와 영역별 도달도 등 평균적인 분석 결과와 대시보드를 제공합니다.', 
        '학급용'
      );
  } else if (route.name === 'ResultStudent') {
      loadingStore.setLoadingMessages(
        '검사결과지 다운로드 (6페이지)', 
        '학생들의 문해력, 수리력의 척도점수와 영역별 도달도 등 평균적인 분석 결과와 대시보드를 제공합니다.',  
        ''
      );
  } else if (route.name === 'ResultPrincipal') {
      loadingStore.setLoadingMessages(
        '검사결과지 다운로드 (12페이지)', 
        '학교 단위로 학생들의 문해력, 수리력의 척도점수와 영역별 도달도 등 평균적인 분석 결과와 대시보드를 제공합니다.',  
        '학교장용'
      );
  }else if (route.name === 'ResultStatus') {
      loadingStore.setLoadingMessages(
        "전체 학생 검사결과지 일괄 다운로드", 
        `문해력, 수리력의 척도 점수, 학년 내 수준, 영역별 도달도 등으로 상세하게 제공합니다.<br> 또한 학년별 검사 점수를 수직척도로 연결하여 학생의 성장 이력을 제공합니다.`,  
        '전체학생용'
      );
  } else if (route.name === 'ResultAnalysis' || route.name === 'ResultAnalysisElementary') {
      loadingStore.setLoadingMessages(
         "우리 학교 반별 진단 결과", 
         `문해력, 수리력의 척도 점수, 학년 내 수준, 영역별 도달도 등으로 상세하게 제공합니다.<br> 또한 학년별 검사 점수를 수직척도로 연결하여 학생의 성장 이력을 제공합니다.`,  
         '우리학교용'
      );
  } else { // 기본 로딩 메시지
      loadingStore.setLoadingMessages(
        "데이터를 불러오는 중...", 
        "잠시만 기다려주세요.",    
        ''         
      );
  }
  console.log(`Loading message set for route: ${route.name}`);
};

onMounted(() => {
    setReportDownloadTypeAndMessage(); // 최초 마운트 시 탭 유형 설정
    isLoadingOverlay.value = false; // 로딩 오버레이 숨기기
    isReportReadyForDownload.value = true; // 다운로드 버튼 활성화
    isMainContentVisible.value = true; // 메인 콘텐츠 보이기
    //startLoadingSimulation(); // 최초 마운트 시 로딩 시뮬레이션 시작
});

onBeforeUnmount(() => { // 컴포넌트가 언마운트될 때 타이머 해제
    if (timerInterval) {
        clearInterval(timerInterval);
    }
    // ResultIndex가 사라질 때 로딩 상태를 정리 (혹시 모를 잔상 방지)
    isLoadingOverlay.value = false;
});

// 다운로드 버튼 관련 로직 - 실제 PDF 생성
const triggerReportDownload = async () => { 
    try {
        console.log('PDF 다운로드 시작 - 라우트:', route.name);
        
        loadingStore.startDownloading();
        
        // 파일명 생성
        let filename = null;
        
        // 학생용 파일명
        if (route.name === 'ResultStudent' && window.currentStudentData) {
            try {
                const studentData = window.currentStudentData;
                const studentName = studentData.user_nm || '학생';
                const schoolName = studentData.schul_nm || '학교';
                const now = new Date();
                const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
                filename = `${schoolName}_${studentName}_진단결과_${dateStr}.pdf`;
            } catch (e) {
                console.warn('학생 데이터 사용 실패:', e);
            }
        }
        
        // 교사용 파일명
        else if (route.name === 'ResultTeacher' && window.currentClassData) {
            try {
                const classData = window.currentClassData;
                const schoolName = classData.schul_nm || '학교';
                const gradeClass = `${route.query.grade_code}-${route.query.school_cls}`;
                const now = new Date();
                const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
                filename = `${schoolName}_${gradeClass}_학급용_검사결과_${dateStr}.pdf`;
            } catch (e) {
                console.warn('학급 데이터 사용 실패:', e);
            }
        }
        
        // 교장용 파일명
        else if (route.name === 'ResultPrincipal' && window.currentSchoolData) {
            try {
                const schoolData = window.currentSchoolData;
                const schoolName = (schoolData.totalList && schoolData.totalList.length > 0) ? 
                    (schoolData.totalList[0].schul_nm || '학교') : '학교';
                const now = new Date();
                const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
                filename = `${schoolName}_학교장용_검사결과_${dateStr}.pdf`;
            } catch (e) {
                console.warn('학교 데이터 사용 실패:', e);
            }
        }
        
        // Status 페이지 - 전체 학생 일괄 다운로드용 파일명
        else if (route.name === 'ResultStatus' && window.currentClassData) {
            try {
                const classData = window.currentClassData;
                const schoolName = classData.schul_nm || '학교';
                const gradeClass = `${route.query.grade_code}-${route.query.school_cls}`;
                const now = new Date();
                const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
                filename = `${schoolName}_${gradeClass}_전체학생_검사결과_${dateStr}.pdf`;
            } catch (e) {
                console.warn('학급 데이터 사용 실패:', e);
            }
        }
        
        // Status 페이지 - 전체 학생 일괄 다운로드 처리
        if (route.name === 'ResultStatus') {
            // Status 페이지에서는 다른 처리가 필요함
            await handleBulkStudentPdfDownload(filename);
            loadingStore.stopDownloading();
            return;
        }
        
        // 기본 파일명 설정
        if (!filename) {
            const now = new Date();
            const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
            if (route.name === 'ResultTeacher') {
                filename = `학급용_검사결과지_${dateStr}.pdf`;
            } else if (route.name === 'ResultPrincipal') {
                filename = `학교장용_검사결과지_${dateStr}.pdf`;
            } else {
                filename = `검사결과지_${dateStr}.pdf`;
            }
        }
        
        // PDF 생성 진행률 업데이트
        const onProgress = (progress) => {
            console.log('PDF 생성 진행률:', progress);
            // 필요시 loadingStore에 진행률 전달 가능
        };
        
        // PDF 생성 및 다운로드
        const result = await downloadResultPDF({
            filename: filename,
            onProgress: onProgress,
            containerSelector: '.result-container',
            pageSelector: '.page-wrapper'
        });
        
        console.log('PDF 다운로드 완료:', result);
        loadingStore.stopDownloading();
        
    } catch (error) {
        console.error('PDF 다운로드 실패:', error);
        loadingStore.stopDownloading();
        
        // 에러 메시지 표시 (필요시)
        alert('PDF 생성 중 오류가 발생했습니다. 다시 시도해주세요.');
    }
}; 

// 동적으로 생성할 학생 결과지 데이터
const bulkStudentResults = ref([]);

// Status 페이지에서 전체 학생 일괄 PDF 다운로드 처리
const handleBulkStudentPdfDownload = async (filename) => {
    try {
        console.log('전체 학생 일괄 PDF 다운로드 시작');
        
        // Status.vue에서 사용하는 데이터 구조 확인
        if (!window.currentClassData) {
            alert('클래스 데이터를 찾을 수 없습니다.');
            return;
        }
        
        if (!window.currentClassData.list) {
            console.log('window.currentClassData.list가 없습니다. 다른 필드를 확인합니다.');
            console.log('사용 가능한 필드들:', Object.keys(window.currentClassData));
            alert('학생 리스트 데이터를 찾을 수 없습니다.');
            return;
        }
        
        const { exam_id } = route.query;
        const studentList = window.currentClassData.list;
        
        console.log('학생 목록:', studentList);
        
        // 각 학생별로 개별 PDF 생성 및 다운로드
        let successCount = 0;
        let failCount = 0;
        
        for (let i = 0; i < studentList.length; i++) {
            const student = studentList[i];

        // 탈퇴학생인 경우 건너뛰기
        if (student.name === '탈퇴학생') {
          console.log(`탈퇴학생 건너뛰기: ${student.name}`);
          continue;
        }
            
            try {
                console.log(`${i + 1}/${studentList.length} - 학생 ${student.user_nm || student.name} 처리 중...`);
                
                // 학생별 API 호출로 데이터 로드
                const response = await api.post('/exam/stu/resultDetail', {
                    exam_id: exam_id,
                    user_id: student.user_id
                });
                
                if (response.data.success) {
                    const studentData = response.data.result.result;
                    
                    // 한 명의 학생 데이터만 설정
                    bulkStudentResults.value = [studentData];
                    
                    // DOM 업데이트 대기
                    await new Promise(resolve => setTimeout(resolve, 800));
                    
                    // 학생별 파일명 생성: 학교_학년_반_번호_이름.pdf
                    const schoolName = (studentData.schul_nm || '학교').replace(/\s+/g, ''); // 공백 제거
                    const grade = route.query.grade_code || studentData.grade_code || '';
                    const classNum = route.query.school_cls || studentData.school_cls || '';
                    const studentNum = studentData.stu_no || (i + 1).toString().padStart(2, '0'); // 학생번호 또는 순번
                    
                    // Status.vue에서 사용하는 student.name 우선 사용
                    const studentName = (
                        student.name || 
                        studentData.user_nm || 
                        `학생${i + 1}`
                    ).replace(/\s+/g, ''); // 공백 제거
                    
                    const studentFilename = `${schoolName}_${grade}_${classNum}_${studentNum}_${studentName}.pdf`;
                    
                    console.log(`학생 ${studentData.user_nm || student.name} PDF 생성 중... (${studentFilename})`);
                    
                    // 개별 학생 PDF 생성 및 다운로드
                    await downloadResultPDF({
                        filename: studentFilename,
                        containerSelector: '.bulk-student-results',
                        pageSelector: '.page-wrapper'
                    });
                    
                    console.log(`학생 ${studentData.user_nm || student.name} PDF 다운로드 완료`);
                    successCount++;
                    
                    // HTML 리셋 및 메모리 정리
                    bulkStudentResults.value = [];
                    await new Promise(resolve => setTimeout(resolve, 500));
                    
                } else {
                    console.error(`학생 ${student.user_nm || student.name} 데이터 로딩 실패:`, response.data.message);
                    failCount++;
                }
                
            } catch (error) {
                console.error(`학생 ${student.user_nm || student.name} 처리 실패:`, error);
                failCount++;
            }
        }
        
        // 완료 알림
        alert(`개별 PDF 다운로드 완료!\n성공: ${successCount}개\n실패: ${failCount}개\n\n각 학생별로 개별 파일이 다운로드되었습니다.`)
        
    } catch (error) {
        console.error('일괄 PDF 다운로드 실패:', error);
        alert('일괄 PDF 다운로드 중 오류가 발생했습니다.');
    } finally {
        // 데이터 정리
        bulkStudentResults.value = [];
    }
};

// 라우터만 이동시키고 로딩 재시작은 watch훅이 담당
const navigateToTeacherMain = () => {
    router.push({ 
        name: 'ResultTeacher',
        query: route.query // 기존 쿼리 파라미터 유지
    }); 
};
const navigateToPrincipalMain = () => {
    router.push({ 
        name: 'ResultPrincipal',
        query: route.query // 기존 쿼리 파라미터 유지
    }); 
};
const navigateToStatus = () => { 
    router.push({ 
        name: 'ResultStatus',
        query: route.query // 기존 쿼리 파라미터 유지
    });
};
const navigateToAnalysis = () => { 
    router.push({ 
        name: 'ResultAnalysis',
        query: route.query // 기존 쿼리 파라미터 유지
    }); 
};

// 현재 라우트에 따라 selected 클래스 적용
const isTeacherMainSelected = computed(() => {
  return route.name === 'ResultTeacher';
});
const isPrincipalMainSelected = computed(() => {
  return route.name === 'ResultPrincipal';
});

// 라우트 변경 감지하여 탭 버튼 업데이트
watch(() => route.name, (newName) => {
  console.log(`[ResultIndex] Route changed to: ${newName}`);
  setReportDownloadTypeAndMessage(); // 라우트 변경 시 메시지 설정 및 탭 유형 업데이트
  isLoadingOverlay.value = false; // 로딩 오버레이 숨기기
  isReportReadyForDownload.value = true; // 다운로드 버튼 활성화
  isMainContentVisible.value = true; // 메인 콘텐츠 보이기
  //startLoadingSimulation(); // 라우트 변경 시 로딩 시뮬레이션 리셋
});
</script>

<template>    
    <div class="top-area pt-5">
                
         <div v-if="loadingStore.reportDownloadType == '학급용' || loadingStore.reportDownloadType == '전체학생용'" class="button-wrap pb-3">           
            <p @click="navigateToTeacherMain" :class="{'selected': isTeacherMainSelected}">학급용</p>               
            <p @click="navigateToStatus" :class="{'selected': route.name === 'ResultStatus'}">우리반 하위 영역별 점수 현황</p>
        </div>
        <div v-if="loadingStore.reportDownloadType == '학교장용' || loadingStore.reportDownloadType == '우리학교용'" class="button-wrap pb-3">            
            <p @click="navigateToPrincipalMain" :class="{'selected': isPrincipalMainSelected}">학교장용</p>                
            <p @click="navigateToAnalysis" :class="{'selected': route.name === 'ResultAnalysis' || route.name === 'ResultAnalysisElementary'}">우리학교 반별 진단결과</p>
        </div>       
        
        <div class="loading">                 
            <div v-if="!isLoadingOverlay && (route.name !== 'ResultAnalysis' && route.name !== 'ResultAnalysisElementary')" class="loading-overlay">
                <div class="loading-content">
                    <div class="top-text pb-4">
                        <h4>{{ loadingStore.mainMessage }}</h4>
                        <p class="sub-message pt-2" v-html="loadingStore.subMessage"></p>    
                    </div>                    
                    <div class="loading-message" v-if="!isReportReadyForDownload">
                        <p class="pb-2">{{ downloadProgressMessage }}</p>                         
                        <div class="loader" aria-label="Loading">
                            <svg viewBox="0 0 100 100" aria-hidden="true">
                                <circle cx="50" cy="50" r="40" />
                                <circle class="top" cx="50" cy="50" r="40" />
                            </svg>
                        </div>  
                        <p class="pt-2"><span class="mark">!</span> 파일 용량이 크므로 생성에 약 10초 소요될 수 있습니다. <br>준비가 완료되면 버튼이 활성화 됩니다.</p>
                    </div>  
                    <button v-show="isReportReadyForDownload" @click="triggerReportDownload" class="download-button" :disabled="loadingStore.isDownloading">
                        {{ loadingStore.downloadButtonText }}
                    </button>
                </div>
            </div>  
        </div>
    </div> 
    <div v-if="shouldShowLayout" class="result-common-wrapper" :class="{ 'visible': isMainContentVisible }">       
        <header class="result-header" v-if="route.name !== 'ResultAnalysis'">           
            <div class="header-content" v-html="headerContent"></div> 
        </header>
        <main>            
            <router-view /> 
        </main>       
    </div>
    
    <!-- 일괄 PDF 다운로드를 위한 숨겨진 학생 결과지들 -->
    <div v-if="bulkStudentResults.length > 0" class="bulk-student-results" style="position: absolute; left: -9999px; top: 0; background: white;">
        <StudentResultComponent 
            v-for="(studentData, index) in bulkStudentResults" 
            :key="studentData.user_id || index"
            :student-data="studentData"
            class="student-result-wrapper"
        />
    </div>
</template>

<style lang="scss" scoped>
.top-area {
    width: 1130px ;
    margin: 0 auto;   
    position: relative;
    z-index: 20;
    .button-wrap {
        display: flex;
        gap: 5px;
        p {
            border: 1px solid #ccc;
            background-color: #fff;
            padding: 3px 12px;
            border-radius: 5px;
            font-size: 16px;
            cursor: pointer;
            &.selected {
                color: #fff;
                background-color: #333;
            }
        }
    }
}
.loading {
    .loading-overlay {
        width: 1130px ;        
        padding: 25px 0 30px;
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        background-color: rgba(var(--result-green-rgb),.05);             
        border-radius: 15px; 
        font-size: 18px;        
    }
    .loading-message {
        display: flex;
        flex-direction: column;
        align-items: center;
        .mark {
            display: inline-block;
            border: 1px solid #000;
            border-radius: 100%;
            width: 20px;
            height: 20px; 
            line-height: 20px;
            text-align: center;
            font-size: 16px;
        }
    }
}
.top-text {
    font-weight: 500;
    line-height: 1.4;
}

// 스피너
.loader {
  width: 50px;
  height: 50px;
}
.loader svg {
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
}
.loader circle {
  fill: none;
  stroke: rgba(var(--result-green-rgb),.1);   
  stroke-width: 10;
}
.loader circle.top {
  stroke: var(--result-green);
  stroke-linecap: round;
  stroke-dasharray: 110 220;
  stroke-dashoffset: 0;
  animation: dash 1.6s ease-in-out infinite;
}
@keyframes dash {
  0% {
    stroke-dasharray: 5 300;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 180 300;
    stroke-dashoffset: -70;
  }
  100% {
    stroke-dasharray: 5 300;
    stroke-dashoffset: -260;
  }
}

.download-button {
    color: #fff;
    background-color: var(--result-green);
    border-radius: 5px;
    padding: 4px 16px;
    cursor: pointer;
}
.result-common-wrapper {
    display: none;
    &.visible {
        display: block;
    }
}
</style>